import * as XLSX from 'xlsx';
import { FortuneSheet } from '../types';
import { liveDims } from './navigation';

/** Excel's hard limits (XFD = 16384th column, 1,048,576th row). Shared by the
 *  tokenizer (shiftFormulaRefs' out-of-grid check) and parseGoToRef. */
export const MAX_EXCEL_COL = 16384;
export const MAX_EXCEL_ROW = 1048576;

/** 1-based column index -> letters ("A", ..., "Z", "AA", ..., "XFD") — wraps
 *  SheetJS's own (0-based) column codec, already a direct dependency and
 *  already used for cell-address conversion in converters.ts. */
export const colIndexToLetter = (index: number): string => XLSX.utils.encode_col(index - 1);

/** Column letters (any case) -> 1-based index ("A" -> 1, "XFD" -> 16384).
 *  `decode_col` is case-sensitive (lowercase decodes as different letters
 *  entirely), so the input is upper-cased first. */
export const letterToColIndex = (letters: string): number => XLSX.utils.decode_col(letters.toUpperCase()) + 1;

/**
 * Mask every character inside a double-quoted string literal with `\0` (same
 * length, so token offsets stay valid) so the ref tokenizer below never treats
 * text inside a formula string (e.g. `="A1"&A1`) as a real reference.
 */
const maskStringLiterals = (formula: string): string => {
    let masked = '';
    let inString = false;
    for (let i = 0; i < formula.length; i += 1) {
        const ch = formula[i];
        if (ch === '"') {
            inString = !inString;
            masked += ch;
        } else {
            masked += inString ? '\u0000' : ch;
        }
    }
    return masked;
};

// Sheet-qualifier prefix: either a quoted name ('Sheet 1'!) or a bare
// identifier (Sheet2!), captured WITH its trailing '!' so it can be
// reproduced byte-identical in cycleRefAtCaret/shiftFormulaRefs.
const SHEET_PREFIX = `(?:'(?:[^']|'')+'|[A-Za-z_][A-Za-z0-9_.]*)!`;
const CELL = `(\\$?)([A-Za-z]{1,3})(\\$?)(\\d{1,7})`;
const REF_RE = new RegExp(`(${SHEET_PREFIX})?${CELL}(?::${CELL})?`, 'g');
// Identifier-boundary checks for a match with no sheet prefix — see their use
// in tokenizeFormula for what each one rules out.
const BEFORE_BOUNDARY_RE = /[A-Za-z0-9_.$]/;
const AFTER_BOUNDARY_RE = /[A-Za-z0-9_]/;

export interface FormulaRefToken {
    start: number;
    end: number;
    /** The exact matched sheet-qualifier text incl. trailing '!', or ''. */
    sheetPrefixRaw: string;
    /** Unquoted, unescaped sheet name, or undefined when unqualified. */
    sheet?: string;
    colAbs1: boolean;
    col1: string;
    rowAbs1: boolean;
    row1: number;
    range?: { colAbs2: boolean; col2: string; rowAbs2: boolean; row2: number };
}

const unquoteSheetName = (raw: string): string => raw.slice(1, -1).replace(/''/g, "'");

/**
 * Finds every A1-style cell/range reference in a formula, skipping string
 * literals and function-call names (a bare identifier immediately followed
 * by '(' — e.g. `LOG10(`, which otherwise matches the same col+row shape).
 * Full-row (`5:5`) and full-column (`C:C`) refs are left untouched (neither
 * matched nor corrupted) — this tokenizer only recognises single-cell refs
 * and cell:cell ranges, which is what cycleRefAtCaret/shiftFormulaRefs/
 * parseGoToRef actually operate on.
 */
export const tokenizeFormula = (formula: string): FormulaRefToken[] => {
    const masked = maskStringLiterals(formula);
    const tokens: FormulaRefToken[] = [];
    REF_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((m = REF_RE.exec(masked))) {
        const [full, sheetPrefixRaw, dollar1, col1, rowDollar1, row1, dollar2, col2, rowDollar2, row2] = m;
        const end = m.index + full.length;
        if (masked[end] === '(') continue; // function name, e.g. LOG10(
        // Identifier boundaries: without a sheet prefix, a letter/digit/'.'/'$'
        // immediately before the match means it's the tail of a longer name
        // (`Revenue2024`) or a number literal's exponent (`1E5`), not a ref —
        // and a letter/digit/'_' immediately after means the match is only a
        // PREFIX of a longer identifier (`A1abc`).
        if (!sheetPrefixRaw && BEFORE_BOUNDARY_RE.test(masked[m.index - 1] ?? '')) continue;
        if (AFTER_BOUNDARY_RE.test(masked[end] ?? '')) continue;
        tokens.push({
            start: m.index,
            end,
            sheetPrefixRaw: sheetPrefixRaw ?? '',
            sheet: sheetPrefixRaw ? unquoteSheetName(sheetPrefixRaw.slice(0, -1)) : undefined,
            colAbs1: dollar1 === '$',
            col1: col1.toUpperCase(),
            rowAbs1: rowDollar1 === '$',
            row1: Number(row1),
            range: col2
                ? { colAbs2: dollar2 === '$', col2: col2.toUpperCase(), rowAbs2: rowDollar2 === '$', row2: Number(row2) }
                : undefined,
        });
    }
    return tokens;
};

const buildCell = (col: string, colAbs: boolean, row: number, rowAbs: boolean): string =>
    `${colAbs ? '$' : ''}${col}${rowAbs ? '$' : ''}${row}`;

// Excel's F4 cycle order: none -> both -> row-only -> col-only -> none.
const ABS_STATE_ORDER: { col: boolean; row: boolean }[] = [
    { col: false, row: false },
    { col: true, row: true },
    { col: false, row: true },
    { col: true, row: false },
];

const sameAbsState = (a: { col: boolean; row: boolean }, b: { col: boolean; row: boolean }): boolean => a.col === b.col && a.row === b.row;

/**
 * Cycles the $/relative state of the ref token under `caret` through
 * Excel's F4 order. A range cycles both ends together, driven by the first
 * cell's current state. No ref under the caret -> returned unchanged.
 */
export const cycleRefAtCaret = (text: string, caret: number): { text: string; caret: number } => {
    const token = tokenizeFormula(text).find((t) => caret >= t.start && caret <= t.end);
    if (!token) return { text, caret };

    const current = { col: token.colAbs1, row: token.rowAbs1 };
    const currentIndex = ABS_STATE_ORDER.findIndex((s) => sameAbsState(s, current));
    const next = ABS_STATE_ORDER[(currentIndex + 1) % ABS_STATE_ORDER.length];

    const cell1 = buildCell(token.col1, next.col, token.row1, next.row);
    const body = token.range ? `${cell1}:${buildCell(token.range.col2, next.col, token.range.row2, next.row)}` : cell1;
    const replacement = token.sheetPrefixRaw + body;

    return {
        text: text.slice(0, token.start) + replacement + text.slice(token.end),
        caret: token.start + replacement.length,
    };
};

const shiftCell = (col: string, colAbs: boolean, row: number, rowAbs: boolean, dr: number, dc: number): string | null => {
    const newRow = rowAbs ? row : row + dr;
    const colIndex = letterToColIndex(col);
    const newColIndex = colAbs ? colIndex : colIndex + dc;
    if (newRow < 1 || newRow > MAX_EXCEL_ROW || newColIndex < 1 || newColIndex > MAX_EXCEL_COL) return null;
    return buildCell(colAbs ? col : colIndexToLetter(newColIndex), colAbs, newRow, rowAbs);
};

/**
 * Ref-shifts every cell/range reference in `formula` by (dr, dc) rows/cols —
 * absolute ($) components never move. A shift that pushes a reference off
 * the grid becomes `#REF!` (that one ref only). String-literal content is
 * left untouched (see tokenizeFormula's masking).
 */
export const shiftFormulaRefs = (formula: string, dr: number, dc: number): string => {
    const tokens = tokenizeFormula(formula);
    if (!tokens.length) return formula;

    let out = '';
    let cursor = 0;
    tokens.forEach((token) => {
        out += formula.slice(cursor, token.start);
        const cell1 = shiftCell(token.col1, token.colAbs1, token.row1, token.rowAbs1, dr, dc);
        if (cell1 === null) {
            out += '#REF!';
        } else if (!token.range) {
            out += token.sheetPrefixRaw + cell1;
        } else {
            const cell2 = shiftCell(token.range.col2, token.range.colAbs2, token.range.row2, token.range.rowAbs2, dr, dc);
            out += cell2 === null ? '#REF!' : `${token.sheetPrefixRaw}${cell1}:${cell2}`;
        }
        cursor = token.end;
    });
    out += formula.slice(cursor);
    return out;
};

export interface GoToRange {
    row: [number, number];
    column: [number, number];
}

export type GoToResult = { ok: true; sheetId?: string; range: GoToRange; clamped: boolean } | { ok: false; error: string };

const GOTO_INVALID: GoToResult = { ok: false, error: "Reference isn't valid." };

const CELL_RE = /^\$?([A-Za-z]{1,3})\$?(\d{1,7})$/;
const RANGE_RE = /^\$?([A-Za-z]{1,3})\$?(\d{1,7}):\$?([A-Za-z]{1,3})\$?(\d{1,7})$/;
const SHEET_QUALIFIER_RE = /^(?:'((?:[^']|'')+)'|([A-Za-z_][A-Za-z0-9_.]*))!(.+)$/;

/**
 * Parses Go To / Name Box input into a selection range. `sheets`/`dims`
 * provide the lookup for a sheet-qualified ref and the clamp bounds for an
 * unqualified one (the currently active sheet's own row/column counts). A
 * ref beyond the sheet's extent clamps to the last row/column rather than
 * erroring or inserting rows.
 */
export const parseGoToRef = (
    text: string,
    sheets: Pick<FortuneSheet, 'id' | 'name' | 'row' | 'column' | 'data'>[],
    dims: { rows: number; cols: number },
): GoToResult => {
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > 300) return GOTO_INVALID;

    let sheetId: string | undefined;
    let refPart = trimmed;
    let targetDims = dims;

    const sheetMatch = SHEET_QUALIFIER_RE.exec(trimmed);
    if (sheetMatch) {
        const sheetName = (sheetMatch[1] ?? sheetMatch[2]).replace(/''/g, "'");
        const sheet = sheets.find((s) => s.name === sheetName);
        if (!sheet) return GOTO_INVALID;
        sheetId = sheet.id;
        refPart = sheetMatch[3];
        targetDims = liveDims(sheet);
    }

    let colA: number;
    let rowA: number;
    let colB: number;
    let rowB: number;
    const rangeMatch = RANGE_RE.exec(refPart);
    if (rangeMatch) {
        colA = letterToColIndex(rangeMatch[1]);
        rowA = Number(rangeMatch[2]);
        colB = letterToColIndex(rangeMatch[3]);
        rowB = Number(rangeMatch[4]);
    } else {
        const cellMatch = CELL_RE.exec(refPart);
        if (!cellMatch) return GOTO_INVALID;
        colA = letterToColIndex(cellMatch[1]);
        rowA = Number(cellMatch[2]);
        colB = colA;
        rowB = rowA;
    }
    if (colA > MAX_EXCEL_COL || colB > MAX_EXCEL_COL || rowA > MAX_EXCEL_ROW || rowB > MAX_EXCEL_ROW) return GOTO_INVALID;
    // "A0" parses cleanly (the row regex is \d{1,7}, which accepts a bare 0)
    // but row/column 0 doesn't exist in Excel's 1-based grid — clampRow/
    // clampCol below only ever clamp DOWN to the sheet's extent, never UP to
    // 1, so an unrejected 0 became `range.row: [-1, -1]`, a negative
    // selection index.
    if (colA < 1 || colB < 1 || rowA < 1 || rowB < 1) return GOTO_INVALID;

    const clampRow = (r: number): number => Math.min(r, Math.max(1, targetDims.rows));
    const clampCol = (c: number): number => Math.min(c, Math.max(1, targetDims.cols));
    const [cRowA, cRowB, cColA, cColB] = [clampRow(rowA), clampRow(rowB), clampCol(colA), clampCol(colB)];
    const clamped = cRowA !== rowA || cRowB !== rowB || cColA !== colA || cColB !== colB;

    return {
        ok: true,
        sheetId,
        clamped,
        range: {
            row: [Math.min(cRowA, cRowB) - 1, Math.max(cRowA, cRowB) - 1],
            column: [Math.min(cColA, cColB) - 1, Math.max(cColA, cColB) - 1],
        },
    };
};

/** Renders a 0-indexed {r,c} cell as its Excel ref text, e.g. {r:4,c:1} -> "B5". */
export const cellToRefText = (r: number, c: number): string => `${colIndexToLetter(c + 1)}${r + 1}`;
